import { Card } from '@/components/forms/ui/card';
import React, { useReducer, useState } from 'react';
import { ExecutionLineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { Button } from '@/components/forms/ui/button';
import { useExecutionContext } from '@/features/executions/execution-context';
import { StandardLineItemMediaTeamCalculatedFields } from '@/features/line-items/line-item-form/standard/standard-line-item-calculated-fields';
import { MgmtFeeLineItemMediaTeamCalculatedFields } from '@/features/line-items/line-item-form/mgmt-fee/mgmt-fee-line-item-calculated-fields';
import { ZeroDollarLineItemMediaTeamCalculatedFields } from '@/features/line-items/line-item-form/zero-dollar/zero-dollar-line-item-calculated-fields';
import { Lock } from '@/icons/lock';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import { formatRange } from 'shared/src/date-string-utils/date-utils';
import { ReviseExecutionButton } from '@/features/executions/revise-io-button';
import { ReadOnlyCustomPacingField } from '@/features/line-items/line-item-form/media-trader-form/read-only-custom-pacing-field';
import { unitLabel } from '@/features/line-items/line-item-form/utils/pacing-display';
import { MediaTraderLineItemUpdate } from 'shared/src/api/line-item-types';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import {
  adjustForCPMById,
  undoCPMById
} from '@/features/line-items/line-item-form/utils/line-item-form-utils';
import { LineItemTypeIndicator } from '@/features/line-items/line-item-form/ui/line-item-type-indicator';
import {
  mediaTraderReducer,
  selectMediaTeamErrors
} from '@/features/line-items/line-item-form/media-trader-form/form-reducer';
import { ButtonMultiSelect } from '@/features/line-items/line-item-form/ui/button-multi-select';
import { platformsForTactics } from 'shared/src/domain/line-item/line-item-channels-filters';
import { tacticById } from 'shared/src/domain/line-item/line-item-channels';
import { UnitCostField } from '@/features/line-items/line-item-form/ui/unit-cost-field';
import { SubmitButton } from '@/features/line-items/line-item-form/ui/submit-button';
import { useScrollToFirstError } from '@/features/line-items/line-item-form/ui/use-scroll-to-first-error';
import { cn } from '@/lib/utils';

type Props = {
  lineItem: ExecutionLineItemTrpcResponse;
  onSubmit: (data: MediaTraderLineItemUpdate) => Promise<void>;
  onClose: () => void;
};

export function MediaTeamLineItemForm({ lineItem, onSubmit, onClose }: Props) {
  const { channels } = useChannelRatesContext();
  const [state, dispatch] = useReducer(mediaTraderReducer, {
    touched: new Set<string>(),
    formData: {
      planned_unit_cost: lineItem.planned_unit_cost,
      media_platform_ids: lineItem.media_platform_ids
    }
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const errors = selectMediaTeamErrors(state);
  const hasErrors = Object.keys(errors).length > 0;

  const errorFor = (field: string): string | undefined => {
    if (!submitAttempted && !state.touched.has(field)) return undefined;
    return errors[field];
  };

  useScrollToFirstError(
    { fieldErrors: errors, formErrors: [], fieldWarnings: {}, formWarnings: [] },
    submitAttempted
  );

  const platformOptions = platformsForTactics(channels, lineItem.tactic_id);
  const tactic = tacticById(channels, lineItem.tactic_id);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitAttempted(true);
    if (hasErrors || isSubmitting) return;
    const parsed = MediaTraderLineItemUpdate.safeParse(state.formData);
    if (!parsed.success) return;
    setIsSubmitting(true);
    try {
      await onSubmit(parsed.data);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Card className="h-full overflow-auto @container">
      <div className="flex w-full min-w-0 flex-col items-center justify-center gap-6 p-6 pt-0">
        <form onSubmit={handleSubmit} className="w-full min-w-0">
          <NameSection
            lineItem={lineItem}
            onClose={onClose}
            isSubmitting={isSubmitting}
            hasErrors={submitAttempted && hasErrors}
          />
          <div className="border-b text-sm text-center py-8 px-2 text-gray-600">
            <span className="font-bold">Media Update</span> allows the Media Team to edit the line
            item's Planned Unit Cost and Media Platforms while an Execution Plan is live
          </div>
          <Section heading="Media Team">
            <CalculatedFields
              lineItem={lineItem}
              planned_unit_cost={state.formData.planned_unit_cost}
            />
            <UnitCostField
              unitPriceTypeId={lineItem.unit_price_type_id}
              value={adjustForCPMById(
                state.formData.planned_unit_cost,
                lineItem.unit_price_type_id
              )}
              onChange={value =>
                dispatch({
                  type: 'SET_PLANNED_UNIT_COST',
                  value: undoCPMById(value, lineItem.unit_price_type_id)
                })
              }
              error={errorFor('planned_unit_cost')}
              onBlur={() => dispatch({ type: 'BLUR_FIELD', field: 'planned_unit_cost' })}
            />

            <div className="flex justify-between gap-6">
              <ButtonMultiSelect<number>
                id="line-item-media-platforms"
                label="Media Platform(s) (Optional)"
                placeholder="Select Tactic for options"
                description={
                  platformOptions.length === 1
                    ? `Only one platform option for ${tactic.name}`
                    : `Select all approved platforms ${tactic.name}`
                }
                value={state.formData.media_platform_ids || []}
                options={platformOptions.map(o => ({ value: o.id, label: o.name }))}
                onChange={ids => dispatch({ type: 'SET_MEDIA_PLATFORMS', value: ids })}
                error={errorFor('media_platform_ids')}
              />
              <ReadOnlyMediaTraders mediaTraders={lineItem.media_traders} />
            </div>
          </Section>
          <Section heading="Notes">
            <ReadOnlyNotes notes={lineItem.notes} />
          </Section>
          <LockedFields lineItem={lineItem} />
        </form>
      </div>
    </Card>
  );
}

type NameSectionProps = {
  lineItem: ExecutionLineItemTrpcResponse;
  isSubmitting: boolean;
  hasErrors: boolean;
  onClose: () => void;
};

function NameSection({ lineItem, onClose, isSubmitting, hasErrors }: NameSectionProps) {
  return (
    <div className="sticky top-0 z-100 mx-[-24px] border-b-[1px] border-gray-200 bg-white py-3 px-6 shadow-[0_1px_4px_0_#00000026]">
      <div className="flex items-center justify-between">
        <div className="font-bold">{lineItem.name}</div>
        <div className="flex items-center gap-2">
          <LineItemTypeIndicator lineItemType={lineItem.line_item_type} />
          <Button
            type="button"
            variant="blueOutline"
            onClick={onClose}
            data-testid="line-item-cancel-button">
            Cancel
          </Button>
          <SubmitButton mode="edit" isSubmitting={isSubmitting} hasErrors={hasErrors} />
        </div>
      </div>
      {hasErrors ? (
        <div
          className="text-destructive flex justify-end text-sm pt-1"
          data-testid="line-item-submit-error">
          Couldn't save — please fix the highlighted fields below
        </div>
      ) : null}
    </div>
  );
}

type CalculatedFieldsProps = {
  lineItem: ExecutionLineItemTrpcResponse;
  planned_unit_cost: number | undefined;
};

function CalculatedFields({ planned_unit_cost, lineItem }: CalculatedFieldsProps) {
  const { execution } = useExecutionContext();
  const { agency_markup_rate, referral_commission_rate } = execution;
  const { price, unit_price, unit_price_type_id, media_budget, estimated_units } = lineItem;

  switch (lineItem.line_item_type) {
    case 'standard':
      return (
        <StandardLineItemMediaTeamCalculatedFields
          inputs={{
            price,
            unit_price: unit_price || undefined,
            unit_price_type_id,
            planned_unit_cost,
            agency_markup_rate,
            referral_commission_rate
          }}
        />
      );
    case 'management_fee':
      return (
        <MgmtFeeLineItemMediaTeamCalculatedFields
          inputs={{
            price,
            media_budget,
            unit_price_type_id,
            planned_unit_cost,
            agency_markup_rate,
            referral_commission_rate
          }}
        />
      );
    case 'zero_dollar':
      return (
        <ZeroDollarLineItemMediaTeamCalculatedFields
          inputs={{
            estimated_units,
            unit_price_type_id,
            planned_unit_cost,
            agency_markup_rate,
            referral_commission_rate
          }}
        />
      );
    case 'zero_margin':
      return null;
  }
}

function LockedFields({ lineItem }: { lineItem: ExecutionLineItemTrpcResponse }) {
  const { unitPriceTypes } = useChannelRatesContext();
  const { execution } = useExecutionContext();
  const referral_commission_rate =
    lineItem.referral_commission_rate ?? execution.referral_commission_rate ?? 0;
  return (
    <div className="mt-2 flex w-full flex-col gap-2 min-w-0">
      <div className="flex items-center gap-2 justify-center py-2">
        <div>To edit the locked fields below, the Account Manager should</div>
        <ReviseExecutionButton />
      </div>
      <LockedField label="Price" value={formatMoney(lineItem.price)} />
      <LockedField label="Channel" value={lineItem.channel_name} />
      <LockedField label="Tactic" value={lineItem.tactic_name} />
      <LockedField label="Unit Price Type" value={lineItem.unit_price_type_name} />
      {lineItem.unit_price != null && (
        <LockedField
          label="Unit Price"
          value={formatMoney(adjustForCPMById(lineItem.unit_price, lineItem.unit_price_type_id))}
        />
      )}
      <LockedField
        label="Estimated Units"
        value={
          <div className="flex items-center gap-2">
            <div>{lineItem.estimated_units.toLocaleString()} </div>
            <div className="capitalize text-xs border-l border-gray-600 pl-2">
              {unitLabel(lineItem.unit_price_type_id, unitPriceTypes)}
            </div>
          </div>
        }
      />
      <LockedField label="Start/End Date" value={formatRange(lineItem.dates)} />
      <LockedField label="Pacing Type" value={<LockedItem value={lineItem.pacing_type} />} />
      {lineItem.pacing_details && lineItem.pacing_details.length > 0 && (
        <ReadOnlyCustomPacingField
          pacing_details={lineItem.pacing_details}
          dates={lineItem.dates}
          unit_price_type_id={lineItem.unit_price_type_id}
          line_item_type={lineItem.line_item_type}
          planned_unit_cost={lineItem.planned_unit_cost}
          planned_margin={lineItem.planned_margin}
          referral_commission_rate={referral_commission_rate}
          monthly_rollover={lineItem.monthly_rollover}
        />
      )}
      <LockedField label="Geo" value={<LockedItem value={lineItem.geo} />} />
      <LockedField label="Targeting" value={<LockedItem value={lineItem.targeting} />} />
      <LockedField label="Audience" value={<LockedItem value={lineItem.audience} />} />
      <LockedField label="Ad Formats" value={<LockedItem value={lineItem.ad_formats} />} />
    </div>
  );
}

export function LockedField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded bg-gray-800 text-white flex justify-between py-2 px-3 items-center w-full min-w-0">
      <div className="font-semibold text-sm text-gray-400 w-36">{label}</div>
      <div className="flex-1 pr-1 w-full min-w-0">{value}</div>
      <div className="border-l-[1px] border-gray-400 pl-2">
        <Lock />
      </div>
    </div>
  );
}

export function LockedItem({ value }: { value: React.ReactNode | string | undefined }) {
  return <div className="rounded bg-slate-700 w-fit inline-block px-2 py-1 text-sm">{value}</div>;
}

function ReadOnlyMediaTraders({ mediaTraders }: { mediaTraders: { id: string; name: string }[] }) {
  return (
    <div
      className="flex w-full min-w-0 flex-col gap-1"
      id="line-item-media-traders"
      data-testid="line-item-media-traders">
      <span className="text-sm font-medium">Media Traders</span>
      <span className="text-muted-foreground text-xs">Managed in Zoho Teams</span>
      {mediaTraders.length === 0 ? (
        <div className="text-sm text-gray-600">No Media Trader on this account's Zoho Team</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {mediaTraders.map(trader => (
            <span
              key={trader.id}
              className="rounded border-[1px] border-gray-300 bg-gray-100 px-2 py-1.5 text-sm text-gray-700">
              {trader.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ReadOnlyNotes({ notes }: { notes: string | null | undefined }) {
  return <div className="rounded bg-gray-100 p-4 text-gray-700">{notes || ''}</div>;
}

type SectionProps = {
  heading: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

export function Section({ heading, description, children, className }: SectionProps) {
  return (
    <div className={cn('flex min-w-0 border-b py-8 last:border-b-0', className)}>
      <div className="mr-4 flex min-w-48 max-w-48 flex-col">
        <div className="mb-4">{heading}</div>
        {description && <div>{description}</div>}
      </div>
      <div className="flex h-full w-full min-w-0 flex-col gap-6 pt-1">{children}</div>
    </div>
  );
}
